import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'templateKey', 'subject', 'bodyHtml', 'bodyText', 'audienceKey',
    'audienceCount', 'audienceSamples', 'selectedPanel', 'userSearch',
    'userResults', 'selectedIds', 'selectedList', 'scheduleField'
  ]

  static values = {
    audienceUrl: String,
    searchUrl: String,
    templateUrl: String
  }

  connect () {
    this.refreshAudience()
    this.toggleSelectedPanel()
  }

  async templateChanged () {
    if (!this.hasTemplateKeyTarget) return
    const key = this.templateKeyTarget.value
    if (!key || !this.templateUrlValue) return

    const url = new URL(this.templateUrlValue, window.location.origin)
    url.searchParams.set('template_key', key)
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
    if (!response.ok) return
    const data = await response.json()

    if (this.hasSubjectTarget) this.subjectTarget.value = data.subject || ''
    if (this.hasBodyHtmlTarget) this.bodyHtmlTarget.value = data.body_html || ''
    if (this.hasBodyTextTarget) this.bodyTextTarget.value = data.body_text || ''

    if (data.audience_key && this.hasAudienceKeyTarget) {
      this.audienceKeyTarget.value = data.audience_key
      if (data.audience_key !== 'selected_users' && this.hasSelectedIdsTarget) {
        this.selectedIdsTarget.value = ''
        if (this.hasSelectedListTarget) this.selectedListTarget.innerHTML = ''
      }
      this.toggleSelectedPanel()
      this.refreshAudience()
    }
  }

  prepareSendNow (event) {
    const form = this.element
    const recipients = this.hasAudienceCountTarget
      ? this.audienceCountTarget.textContent.trim()
      : 'your selected audience'
    const subject = this.hasSubjectTarget ? this.subjectTarget.value.trim() : ''

    form.dataset.confirmTitle = 'Send broadcast now?'
    form.dataset.confirmMessage = [
      subject ? `Subject: “${subject}”` : null,
      `This will queue emails for ${recipients}. Delivery runs in the background and cannot be undone.`
    ].filter(Boolean).join('\n\n')
    form.dataset.confirmButton = 'Yes, send now'
    form.dataset.confirmDestructive = 'false'
  }

  audienceChanged () {
    this.toggleSelectedPanel()
    this.refreshAudience()
  }

  toggleSelectedPanel () {
    if (!this.hasSelectedPanelTarget || !this.hasAudienceKeyTarget) return
    const selected = this.audienceKeyTarget.value === 'selected_users'
    this.selectedPanelTarget.classList.toggle('hidden', !selected)
  }

  async refreshAudience () {
    if (!this.hasAudienceKeyTarget || !this.audienceUrlValue) return

    const url = new URL(this.audienceUrlValue, window.location.origin)
    url.searchParams.set('audience_key', this.audienceKeyTarget.value)
    if (this.hasSelectedIdsTarget) {
      url.searchParams.set('user_ids', this.selectedIdsTarget.value)
    }

    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
    if (!response.ok) return
    const data = await response.json()

    if (this.hasAudienceCountTarget) {
      this.audienceCountTarget.textContent = `${data.count} recipient${data.count === 1 ? '' : 's'}`
    }

    if (this.hasAudienceSamplesTarget) {
      if (!data.samples || data.samples.length === 0) {
        this.audienceSamplesTarget.innerHTML = '<p class="text-xs text-slate-500">No sample recipients yet.</p>'
      } else {
        this.audienceSamplesTarget.innerHTML = data.samples.map((user) => (
          `<li class="truncate text-xs text-slate-600">${this.escape(user.name)} · ${this.escape(user.email)}</li>`
        )).join('')
      }
    }
  }

  async searchUsers () {
    if (!this.hasUserSearchTarget || !this.searchUrlValue) return
    const query = this.userSearchTarget.value.trim()
    if (query.length < 2) {
      if (this.hasUserResultsTarget) this.userResultsTarget.innerHTML = ''
      return
    }

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set('q', query)
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
    if (!response.ok) return
    const data = await response.json()

    if (!this.hasUserResultsTarget) return
    this.userResultsTarget.innerHTML = (data.users || []).map((user) => (
      `<button type="button" class="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-admin-50" data-action="email-broadcast-composer#addUser" data-id="${user.id}" data-email="${this.escape(user.email)}" data-name="${this.escape(user.name)}">
        <span class="min-w-0 truncate"><span class="font-semibold text-slate-800">${this.escape(user.name)}</span> <span class="text-slate-500">${this.escape(user.email)}</span></span>
        <span class="shrink-0 text-xs font-bold text-admin-700">Add</span>
      </button>`
    )).join('')
  }

  addUser (event) {
    const button = event.currentTarget
    const id = button.dataset.id
    const email = button.dataset.email
    const name = button.dataset.name
    const ids = this.currentIds()
    if (ids.includes(id)) return

    ids.push(id)
    this.selectedIdsTarget.value = ids.join(',')
    if (this.hasSelectedListTarget) {
      this.selectedListTarget.insertAdjacentHTML('beforeend', this.chipHtml(id, name, email))
    }
    this.refreshAudience()
  }

  removeUser (event) {
    const id = event.currentTarget.dataset.id
    const ids = this.currentIds().filter((value) => value !== id)
    this.selectedIdsTarget.value = ids.join(',')
    const chip = this.element.querySelector(`[data-selected-user="${id}"]`)
    if (chip) chip.remove()
    this.refreshAudience()
  }

  currentIds () {
    if (!this.hasSelectedIdsTarget) return []
    return this.selectedIdsTarget.value.split(',').map((value) => value.trim()).filter(Boolean)
  }

  chipHtml (id, name, email) {
    return `<span data-selected-user="${id}" class="inline-flex max-w-full items-center gap-2 rounded-full bg-admin-50 px-3 py-1 text-xs font-semibold text-admin-800 ring-1 ring-admin-200">
      <span class="truncate">${this.escape(name)} · ${this.escape(email)}</span>
      <button type="button" class="shrink-0 text-admin-700 hover:text-rose-600" data-action="email-broadcast-composer#removeUser" data-id="${id}" aria-label="Remove">×</button>
    </span>`
  }

  escape (value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
  }
}
